import { AmbientLight, Layer, LightingEffect } from "@deck.gl/core";
import { Tile3DLayer, TileLayer } from "@deck.gl/geo-layers";
import { MapboxOverlay } from "@deck.gl/mapbox";
import { Map } from "@iventis/mapbox-gl";

/** Handles the adding/removing of Deckgl to/from Mapbox */
export class DeckglMapboxIntegration {
    private readonly mapboxMap: Map;

    /** Mapbox overlay displayed on an external canvas */
    private externalOverlay: MapboxOverlay;

    private externalLayers: Layer[] = [];

    /** Mapbox overlay displayed on the Mapbox canvas */
    private interleavedOverlay: MapboxOverlay;

    private interleavedLayers: Layer[] = [];

    /** Makes DeckGl lighting to be more like Mapbox */
    private lightingEffect = new LightingEffect({
        ambientLight: new AmbientLight({
            color: [255, 255, 255],
            intensity: 3,
        }),
    });

    constructor(mapboxMap: Map) {
        this.mapboxMap = mapboxMap;
    }

    public hasExternalOverlay() {
        return this.externalOverlay != null;
    }

    public addLayerToInterleavedOverlay(layer: Layer) {
        this.interleavedLayers.push(layer);
        this.getInterleavedOverlay().setProps({
            // Deconstruct array otherwise DeckGl does not update it's layers
            layers: [...this.interleavedLayers],
        });
    }

    public addLayerToExternalOverlay(layer: Layer) {
        this.externalLayers.push(layer);
        const sortedExternalLayers = this.getSortedExternalLayers();
        this.getExternalOverlay().setProps({
            // Deconstruct array otherwise DeckGl does not update it's layers
            layers: [...sortedExternalLayers],
        });
    }

    public removeLayer(layerId: string) {
        if (this.externalLayers.some((layer) => layer.id === layerId)) {
            this.externalLayers = this.externalLayers.filter((layer) => layer.id !== layerId);
            const sortedExternalLayers = this.getSortedExternalLayers();
            this.getExternalOverlay().setProps({
                // Deconstruct array otherwise DeckGl does not update it's layers
                layers: [...sortedExternalLayers],
            });
        }

        if (this.interleavedLayers.some((layer) => layer.id === layerId)) {
            this.interleavedLayers = this.interleavedLayers.filter((layer) => layer.id !== layerId);
            this.getInterleavedOverlay().setProps({
                // Deconstruct array otherwise DeckGl does not update it's layers
                layers: [...this.interleavedLayers],
            });
        }

        // If there are no layers left to display, remove the interleaved overlay
        if (this.interleavedLayers.length === 0 && this.interleavedOverlay != null) {
            this.interleavedOverlay.finalize();
        }
    }

    /** Checks if a given layer is loaded, if the layer can't be found then false is returned */
    public isLayerLoaded(layerId: string) {
        const interleavedLayer = this.interleavedLayers.find((l) => l.id === layerId);
        const externalLayer = this.externalLayers.find((l) => l.id === layerId);

        if (interleavedLayer) {
            return interleavedLayer.isLoaded;
        }

        if (externalLayer) {
            return externalLayer.isLoaded;
        }

        return false;
    }

    /** Lists all Deckgl rendered objects in the current viewport */
    public getDeckglMapObjectsOnScreen() {
        const isEveryLayerLoaded = this.interleavedLayers.every((l) => l.isLoaded) && this.externalLayers.every((l) => l.isLoaded);

        if (isEveryLayerLoaded) {
            const interleavedObjects = this.interleavedOverlay
                ? this.interleavedOverlay.pickObjects({
                      x: 0,
                      y: 0,
                      layerIds: this.interleavedLayers.map((l) => l.id),
                      width: this.mapboxMap.getCanvas().clientWidth,
                      height: this.mapboxMap.getCanvas().clientHeight,
                  })
                : [];

            const externalObjects = this.externalOverlay
                ? this.externalOverlay.pickObjects({
                      x: 0,
                      y: 0,
                      layerIds: this.externalLayers.map((l) => l.id),
                      width: this.externalOverlay.getCanvas().clientWidth,
                      height: this.externalOverlay.getCanvas().clientHeight,
                  })
                : [];
            return [...interleavedObjects, ...externalObjects];
        }

        return [];
    }

    public getSnapshotDataUrl() {
        const canvas = this.externalOverlay.getCanvas();
        return canvas.toDataURL();
    }

    /**
     * Checks if a Deckgl interleaved overlay has been added
     * If it has return it
     * If it hasn't then create one
     */
    private getInterleavedOverlay() {
        if (this.interleavedOverlay) {
            return this.interleavedOverlay;
        }

        this.interleavedOverlay = new MapboxOverlay({
            interleaved: true,
            effects: [this.lightingEffect],
            layers: [],
            // Ensure we are always using mapbox's cursor value
            getCursor: () => this.mapboxMap.getCanvas()?.style.cursor,
        });

        // On remove ensure the global var is set to undefined
        this.interleavedOverlay.onRemove = () => {
            this.interleavedOverlay = undefined;
            // Readd the fog
            this.mapboxMap.setFog({});
        };

        this.mapboxMap.addControl(this.interleavedOverlay);
        // Set fog to be undefined so we can see the below canvas
        this.mapboxMap.setFog(undefined);

        return this.interleavedOverlay;
    }

    /**
     * Checks if a Deckgl external overlay has been added
     * If it has return it
     * If it hasn't then create one
     */
    private getExternalOverlay() {
        if (this.externalOverlay) {
            return this.externalOverlay;
        }

        this.externalOverlay = new MapboxOverlay({
            interleaved: false,
            effects: [this.lightingEffect],
            layers: [],
            // Ensure we are always using mapbox's cursor value
            getCursor: () => this.mapboxMap.getCanvas()?.style.cursor,
            onLoad: () => this.reorderDeckglCanvasDOMPosition(),
        });

        // On remove ensure the global var is set to undefined
        this.externalOverlay.onRemove = () => {
            this.externalOverlay = undefined;
        };

        this.mapboxMap.addControl(this.externalOverlay);
        return this.externalOverlay;
    }

    /** Sorts the DeckGl layers to ensure 3D layers are above the 2D layers */
    private getSortedExternalLayers() {
        const getOrderValue = (layer: Layer<{}>) => {
            if (layer instanceof Tile3DLayer) {
                return 1;
            }
            if (layer instanceof TileLayer) {
                return 0;
            }
            throw new Error(`Layer instance not recongised`);
        };

        return this.externalLayers.sort((layerA, layerB) => getOrderValue(layerA) - getOrderValue(layerB));
    }

    /** Removes the Deckgl canvas and tooltip from the DOM */
    private removeExternalDeckgElementsFromDOM() {
        // Manually need to remove the Deckgl canvas from the DOM
        const deckCanvas = document.getElementById("deckgl-overlay");
        if (deckCanvas) {
            deckCanvas.parentNode.removeChild(deckCanvas);
        }

        // Manually need to remove each Deckgl tooltip from the DOM
        const deckTooltipElements = document.getElementsByClassName("deck-tooltip");
        if (deckTooltipElements) {
            for (const tooltip of Array.from(deckTooltipElements)) {
                const deckTooltipContainer = tooltip.parentNode.parentNode;
                deckTooltipContainer.parentNode.removeChild(deckTooltipContainer);
            }
        }
    }

    /**
     * Gets the external Deckgl canvas and places it behind the Mapbox Canvas in the DOM
     * Ensures that the 3D sitemap is displayed correctly
     */
    private reorderDeckglCanvasDOMPosition() {
        const deckCanvas = this.externalOverlay.getCanvas();
        const mapCanvasContainer = this.mapboxMap.getCanvasContainer();
        const mapCanvas = this.mapboxMap.getCanvas();
        if (!deckCanvas || !mapCanvasContainer || !mapCanvas) {
            return;
        }
        mapCanvasContainer.insertBefore(deckCanvas, mapCanvasContainer.firstChild);
        deckCanvas.style.position = "absolute";
        deckCanvas.style.zIndex = "-1";
        mapCanvas.style.zIndex = "0";
        deckCanvas.style.pointerEvents = "none";
    }

    public remove() {
        this.externalOverlay?.finalize();
        this.removeExternalDeckgElementsFromDOM();
        this.interleavedOverlay?.finalize();
    }
}
